import { Link } from "react-router-dom";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import type { PublicStory } from "@/lib/publicStories";

export default function FeaturedStory({ story }: { story: PublicStory }) {
  return (
    <section className="sun-glaze bg-background-50 py-20 md:py-28">
      <div className="w-full px-4 md:px-8">
        <Reveal className="flex items-center gap-3 mb-10">
          <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
          <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
            Featured Story
          </span>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="rounded-lg overflow-hidden w-full h-80 md:h-[30rem]">
              <img
                src={story.cover}
                width={1200}
                height={800}
                alt={`${story.title} — featured Rythugaru farmer story cover image from ${story.village}`}
                title="Featured farmer story from Rythugaru"
                className="w-full h-full object-cover object-top"
                decoding="async"
              />
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={120}>
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="bg-accent-500 text-background-50 text-[11px] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                {story.category}
              </span>
              <span className="text-xs text-foreground-500">
                {story.date} · {story.readTime}
              </span>
            </div>

            <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
              {story.title}
            </h2>

            <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
              {story.excerpt}
            </p>

            {story.quote ? (
              <blockquote className="mt-7 border-l-2 border-accent-500 pl-5">
                <p className="font-heading text-xl md:text-2xl italic text-foreground-800 leading-snug">
                  "{story.quote}"
                </p>
                <footer className="text-sm text-foreground-600 mt-3">
                  — {story.author}, {story.village}
                </footer>
              </blockquote>
            ) : null}

            <Link
              to={`/stories/${story.slug}`}
              className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line"></i>
              Read the full story
              <i className="ri-arrow-right-line"></i>
            </Link>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/experiences/farmer-for-a-day"
                className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-4 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Experience this farm
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/farmers"
                className="inline-flex items-center justify-center gap-2 bg-background-50 border border-background-300 text-foreground-900 text-sm font-semibold px-7 py-4 rounded-md hover:border-primary-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                Meet the farmers
                <i className="ri-user-heart-line"></i>
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}